import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Edit2, HelpCircle, Trash2, ArrowLeft, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI, showConfirmModal } from '../services/api';
import { readResponse } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectTypes = () => {
  const navigate = useNavigate();
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [color, setColor] = useState('FFFFFF');
  const [commission, setCommission] = useState('');
  const [editingName, setEditingName] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const isAdmin = authAPI.isAdmin();

  useEffect(() => {
    fetchProjectTypes();
  }, []);

  const fetchProjectTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/project-types`);
      const { data } = await readResponse(res);
      setProjectTypes(data.project_types || []);
    } catch (err) {
      toast.error('Failed to load project types');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setColor('FFFFFF');
    setCommission('');
    setEditingName(null);
    setEditingId(null);
    setFormOpen(false);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Project Type name is required');
      return;
    }

    try {
      if (editingName) {
        // Operate by unique _id so a duplicate/whitespace name in the data
        // can't cause the update to silently miss the intended row.
        const res = await fetch(`${API_URL}/api/project-types/${encodeURIComponent(editingId || editingName)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: name.trim(), color: color.replace('#', ''), commission: parseFloat(commission) || 0 })
        });
        if (!res.ok) {
          const { data: err } = await readResponse(res);
          toast.error((err && err.detail) || 'Failed to update project type');
          return;
        }
        toast.success('Project type updated');
      } else {
        const res = await fetch(`${API_URL}/api/project-types`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: name.trim(), color: color.replace('#', ''), commission: parseFloat(commission) || 0 })
        });
        if (!res.ok) {
          const { data: err } = await readResponse(res);
          toast.error((err && err.detail) || 'Failed to create');
          return;
        }
        toast.success('Project type created');
      }
      resetForm();
      fetchProjectTypes();
    } catch (err) {
      toast.error('Failed to save project type');
    }
  };

  const handleEdit = (pt) => {
    setName(pt.name);
    setColor(pt.color);
    setCommission(String(pt.commission));
    setEditingName(pt.name);
    setEditingId(pt._id || null);
    setFormOpen(true);
  };

  const handleDelete = async (pt) => {
    if (!(await showConfirmModal(`Delete project type "${pt.name}"?`))) return;
    try {
      const res = await fetch(`${API_URL}/api/project-types/${encodeURIComponent(pt._id || pt.name)}`, { method: 'DELETE' });
      if (!res.ok) { toast.error('Failed to delete'); return; }
      toast.success('Project type deleted');
      fetchProjectTypes();
    } catch (err) {
      toast.error('Failed to delete project type');
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/setup')}
            className="flex items-center gap-1 text-sm text-[#5B8DB8] hover:text-[#4A7CA5] mb-2 transition-colors"
            data-testid="back-to-setup-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Setup
          </button>
          <h2 className="text-lg text-[#5B8DB8] font-light">Setup</h2>
          <h1 className="text-3xl text-gray-700 font-light italic">Job Type Setup</h1>
        </div>

        <div className="bg-white rounded shadow p-8">
          {/* Hint */}
          <div className="mb-8 flex items-start gap-2">
            <span className="bg-[#FFA94D] text-white text-xs font-bold px-2 py-1 rounded">Hint</span>
            <p className="text-gray-600 text-sm">
              Create job types such as Fire, Water, Mold also create unique color for each type. Organizations implementing a commission based system shall enter a commission percentage value for each job type. Organizations not using a commission compensation system shall leave the commission value at 0. Note project types, colors and percentages are editable at any time.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Left - Add Job Type button / Form */}
            <div className="w-full lg:w-80 flex-shrink-0">
              {!formOpen ? (
                <button
                  onClick={() => setFormOpen(true)}
                  className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white text-sm font-medium px-5 py-2 rounded transition-colors flex items-center gap-2"
                  data-testid="add-job-type-btn"
                >
                  <Plus size={16} /> Add Job Type
                </button>
              ) : (
                <div className="border border-gray-200 rounded p-4 bg-gray-50" data-testid="job-type-form">
                  <div className="text-[#5B8DB8] text-sm font-medium mb-3" data-testid="job-type-form-header">
                    {editingName ? 'Edit Job Type' : 'New Job Type'}
                  </div>
                  <div className="space-y-5">
                    <div className="flex items-center gap-4">
                      <label className="text-[#5B8DB8] text-sm w-28 text-right">Project Type:</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') resetForm(); }}
                        autoFocus
                        className="border border-gray-300 rounded px-3 py-2 flex-1"
                        data-testid="project-type-name-input"
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-[#5B8DB8] text-sm w-28 text-right">Color:</label>
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="text"
                          value={color}
                          onChange={(e) => setColor(e.target.value.replace('#', ''))}
                          className="border border-gray-300 rounded px-3 py-2 flex-1"
                          data-testid="project-type-color-input"
                        />
                        <input
                          type="color"
                          value={`#${(color || 'ffffff').padStart(6, '0').slice(0, 6)}`}
                          onChange={(e) => setColor(e.target.value.replace('#', '').toUpperCase())}
                          className="w-9 h-9 p-0 border border-gray-300 rounded cursor-pointer bg-white"
                          title="Pick a color"
                          data-testid="project-type-color-picker"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-[#5B8DB8] text-sm w-28 text-right">
                        Commission %<br /><span className="text-xs">(0-100)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={commission}
                          onChange={(e) => setCommission(e.target.value)}
                          className="border border-gray-300 rounded px-3 py-2 w-24"
                          data-testid="project-type-commission-input"
                        />
                        <HelpCircle className="w-5 h-5 text-[#5B8DB8]" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      <button
                        onClick={handleSubmit}
                        disabled={!name.trim()}
                        className="bg-[#FFA94D] hover:bg-[#F08A2A] disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-1.5 rounded text-sm transition-colors"
                        data-testid="save-job-type-btn"
                      >
                        {editingName ? 'Save Changes' : 'Save Job Type'}
                      </button>
                      <button
                        onClick={resetForm}
                        className="border border-gray-300 hover:bg-gray-100 text-gray-600 text-sm px-4 py-1.5 rounded transition-colors"
                        data-testid="cancel-job-type-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right - Table */}
            <div className="flex-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2">
                    <th className="text-left p-3 text-gray-600 font-medium">Project Type</th>
                    <th className="text-left p-3 text-gray-600 font-medium">Color</th>
                    <th className="text-left p-3 text-gray-600 font-medium">Commission % (0-100)</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="4" className="p-4 text-center text-gray-500">Loading...</td></tr>
                  ) : projectTypes.length === 0 ? (
                    <tr><td colSpan="4" className="p-4 text-center text-gray-500 italic">No project types found</td></tr>
                  ) : (
                    projectTypes.map((pt, idx) => (
                      <tr key={idx} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="p-3 text-gray-700">{pt.name}</td>
                        <td className="p-3">
                          <div
                            className="w-10 h-7 border border-gray-200"
                            style={{ backgroundColor: `#${pt.color}` }}
                          />
                        </td>
                        <td className="p-3 text-gray-700">{pt.commission}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEdit(pt)}
                              className="border border-gray-300 rounded p-1 hover:bg-gray-100"
                              data-testid={`edit-${pt.name.toLowerCase().replace(/[\s/]+/g, '-')}`}
                            >
                              <Edit2 className="w-4 h-4 text-gray-500" />
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => handleDelete(pt)}
                                className="border border-gray-300 rounded p-1 hover:bg-red-50"
                                data-testid={`delete-${pt.name.toLowerCase().replace(/[\s/]+/g, '-')}`}
                              >
                                <Trash2 className="w-4 h-4 text-[#DC3545]" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProjectTypes;
